/** Contains reusable Zod schema extensions, such as schemas that log and strip unknown keys. */
// @ts-check
import { z } from 'zod';

/**
 * Creates a Zod schema that logs a warning for unknown keys and then strips them.
 * This provides a more robust alternative to .strict() by cleaning data instead of throwing an error.
 *
 * @template {z.ZodRawShape} T
 * @param {z.ZodObject<T>} schema - The Zod object schema.
 * @returns {z.ZodType<z.infer<z.ZodObject<T>>, any, any>} - A new Zod schema with the desired behavior.
 */
export const createLoggedStrippedSchema = (schema) => {
  // Use .catchall(z.any()) to allow unknown keys to pass through to the transform stage.
  // This is the modern replacement for .passthrough().
  return schema.catchall(z.any()).transform((data) => {
    const shapeKeys = Object.keys(schema.shape);
    const dataKeys = Object.keys(data);
    const unknownKeys = dataKeys.filter((key) => !shapeKeys.includes(key));

    if (unknownKeys.length > 0) {
      console.warn(
        `[Zod Warning] Unknown keys found and stripped:`,
        unknownKeys.join(', '),
      );
    }

    // Return a new object with only the allowed keys
    const strippedData = {};
    for (const key of shapeKeys) {
      if (Object.prototype.hasOwnProperty.call(data, key)) {
        strippedData[key] = data[key];
      }
    }
    return strippedData;
  });
};
